import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  SapEquipmentRow,
  ParsedEquipment,
  FunctionalLocation,
} from '@easydone/shared';
import { useAuth } from '../contexts/AuthContext';
import { useOrg } from '../contexts/OrgContext';
import { parseSapEquipment, importEquipment } from '../services/sapImportService';
import * as XLSX from 'xlsx';
import {
  ArrowUpTrayIcon,
  CheckCircleIcon,
  XCircleIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';

type ImportStep = 'upload' | 'mapping' | 'importing' | 'complete';

export default function EquipmentImportPage() {
  const { currentUser } = useAuth();
  const { currentOrg } = useOrg();
  const navigate = useNavigate();

  // Step management
  const [step, setStep] = useState<ImportStep>('upload');

  // File upload
  const [equipmentFile, setEquipmentFile] = useState<File | null>(null);
  const [equipmentRows, setEquipmentRows] = useState<SapEquipmentRow[]>([]);
  const [parsedEquipment, setParsedEquipment] = useState<ParsedEquipment[]>([]);

  // FLOCs for mapping
  const [flocs, setFlocs] = useState<FunctionalLocation[]>([]);
  const [flocCodeMap, setFlocCodeMap] = useState<Map<string, string>>(new Map());

  // Mapping configuration
  const [autoMapByCode, setAutoMapByCode] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Progress
  const [progress, setProgress] = useState({ current: 0, total: 0, message: '' });
  const [importResult, setImportResult] = useState<{
    success: boolean;
    imported: number;
    errors: string[];
  } | null>(null);

  const [error, setError] = useState<string>('');

  // Handle file upload
  const handleFileUpload = async (file: File) => {
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      setEquipmentFile(file);
      setEquipmentRows(jsonData as SapEquipmentRow[]);
      setError('');
    } catch (err: any) {
      setError(`Failed to read file: ${err.message}`);
    }
  };

  // Load FLOCs for mapping
  const loadFlocs = async () => {
    if (!currentOrg) return;

    try {
      const flocsRef = collection(db, 'organizations', currentOrg.id, 'functionalLocations');
      const snapshot = await getDocs(flocsRef);
      const loadedFlocs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as FunctionalLocation));

      setFlocs(loadedFlocs);

      // Build code-to-ID map for auto-mapping
      const codeMap = new Map<string, string>();
      loadedFlocs.forEach(floc => {
        codeMap.set(floc.code, floc.id);
      });
      setFlocCodeMap(codeMap);
    } catch (err: any) {
      setError(`Failed to load FLOCs: ${err.message}`);
    }
  };

  // Proceed to mapping
  const handleProceedToMapping = async () => {
    if (!equipmentRows.length) {
      setError('Please upload an equipment file');
      return;
    }

    try {
      // Parse equipment
      const parsed = parseSapEquipment(equipmentRows, {
        siteCode: '',
        siteName: '',
        defaultFlocType: 'custom',
        defaultCriticality: 'medium',
        defaultStatus: 'operational',
        importEquipment: true,
      });

      setParsedEquipment(parsed);

      // Load FLOCs for mapping
      await loadFlocs();

      setStep('mapping');
      setError('');
    } catch (err: any) {
      setError(`Failed to parse equipment: ${err.message}`);
    }
  };

  // Execute import
  const handleImport = async () => {
    if (!currentOrg || !currentUser) return;

    setStep('importing');
    setProgress({ current: 0, total: parsedEquipment.length, message: 'Starting import...' });

    try {
      const result = await importEquipment(
        currentOrg.id,
        '',
        parsedEquipment,
        flocCodeMap,
        (current, total, message) => {
          setProgress({ current, total, message });
        }
      );

      setImportResult(result);
      setStep('complete');
    } catch (err: any) {
      setError(`Import failed: ${err.message}`);
      setStep('mapping');
    }
  };

  // Get mapped FLOC for equipment
  const getMappedFloc = (equip: ParsedEquipment): FunctionalLocation | null => {
    if (!equip.flocCode) return null;
    const flocId = flocCodeMap.get(equip.flocCode);
    if (!flocId) return null;
    return flocs.find(f => f.id === flocId) || null;
  };

  // Filter equipment for display
  const filteredEquipment = parsedEquipment.filter(equip =>
    equip.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    equip.serialNumber.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!currentOrg) {
    return (
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="text-center">Please select an organization first.</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Equipment Import</h1>
        <p className="mt-1 text-sm text-gray-600">
          Import equipment/assets from SAP export and link to functional locations
        </p>
      </div>

      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-center">
          {['upload', 'mapping', 'importing', 'complete'].map((s, idx) => (
            <div key={s} className="flex items-center">
              <div
                className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                  step === s
                    ? 'border-primary-600 bg-primary-600 text-white'
                    : idx < ['upload', 'mapping', 'importing', 'complete'].indexOf(step)
                    ? 'border-primary-600 bg-primary-600 text-white'
                    : 'border-gray-300 bg-white text-gray-400'
                }`}
              >
                {idx + 1}
              </div>
              {idx < 3 && (
                <div
                  className={`w-20 h-0.5 ${
                    idx < ['upload', 'mapping', 'importing', 'complete'].indexOf(step)
                      ? 'bg-primary-600'
                      : 'bg-gray-300'
                  }`}
                />
              )}
            </div>
          ))}
        </div>
        <div className="flex items-center justify-center mt-2">
          {['Upload', 'Mapping', 'Importing', 'Complete'].map((label, idx) => (
            <div key={label} className="flex items-center">
              <span
                className={`text-xs ${
                  ['upload', 'mapping', 'importing', 'complete'].indexOf(step) === idx
                    ? 'text-primary-600 font-medium'
                    : 'text-gray-500'
                }`}
              >
                {label}
              </span>
              {idx < 3 && <span className="mx-4" />}
            </div>
          ))}
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <XCircleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-900">Error</p>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* Step 1: Upload File */}
      {step === 'upload' && (
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Step 1: Upload Equipment File</h2>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Equipment Export File (Excel) *
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0])}
                className="hidden"
                id="equipment-file-upload"
              />
              <label
                htmlFor="equipment-file-upload"
                className="cursor-pointer flex flex-col items-center"
              >
                <ArrowUpTrayIcon className="w-12 h-12 text-gray-400 mb-2" />
                <span className="text-sm text-gray-600">
                  Click to upload or drag and drop
                </span>
                <span className="text-xs text-gray-500 mt-1">Excel files (.xlsx, .xls)</span>
              </label>
              {equipmentFile && (
                <div className="mt-3 flex items-center justify-center gap-2 text-sm text-green-600">
                  <CheckCircleIcon className="w-5 h-5" />
                  <span>{equipmentFile.name} ({equipmentRows.length} rows)</span>
                </div>
              )}
            </div>
          </div>

          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-6">
            <p className="text-sm text-blue-900">
              <strong>Tip:</strong> Make sure you've already imported FLOCs before importing equipment.
              Equipment will be linked to FLOCs in the next step.
            </p>
          </div>

          <div className="flex gap-3 justify-end">
            <button onClick={() => navigate(-1)} className="btn btn-secondary">
              Cancel
            </button>
            <button
              onClick={handleProceedToMapping}
              disabled={!equipmentRows.length}
              className="btn btn-primary"
            >
              Next: Map to FLOCs
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Mapping */}
      {step === 'mapping' && (
        <div className="space-y-6">
          {/* Mapping Options */}
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Step 2: Link Equipment to FLOCs</h2>

            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="text-sm font-medium text-gray-900">Auto-map by FLOC Code</p>
                  <p className="text-xs text-gray-600 mt-1">
                    Automatically link equipment to FLOCs using the "Functional Loc." column from your Excel
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={autoMapByCode}
                  onChange={(e) => setAutoMapByCode(e.target.checked)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-gray-900">{parsedEquipment.length}</div>
                <div className="text-sm text-gray-600">Total Equipment</div>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">
                  {parsedEquipment.filter(e => e.flocCode && flocCodeMap.has(e.flocCode)).length}
                </div>
                <div className="text-sm text-gray-600">Mapped to FLOCs</div>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-gray-400">
                  {parsedEquipment.filter(e => !e.flocCode || !flocCodeMap.has(e.flocCode)).length}
                </div>
                <div className="text-sm text-gray-600">Not Mapped</div>
              </div>
            </div>

            {/* Search */}
            <div className="mb-4">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search equipment..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input pl-10"
                />
              </div>
            </div>

            {/* Equipment Preview Table */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Equipment #
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Description
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      FLOC Code
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Mapped FLOC
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredEquipment.slice(0, 20).map((equip, idx) => {
                    const mappedFloc = getMappedFloc(equip);
                    const isMapped = !!mappedFloc;

                    return (
                      <tr key={idx}>
                        <td className="px-4 py-3 text-sm text-gray-900">{equip.serialNumber}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{equip.name}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{equip.flocCode || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {mappedFloc ? mappedFloc.name : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {isMapped ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                              Mapped
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                              Not Mapped
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {filteredEquipment.length > 20 && (
              <p className="text-sm text-gray-500 mt-3 text-center">
                Showing first 20 of {filteredEquipment.length} equipment items
              </p>
            )}
          </div>

          <div className="flex gap-3 justify-end">
            <button onClick={() => setStep('upload')} className="btn btn-secondary">
              Back
            </button>
            <button onClick={handleImport} className="btn btn-primary">
              Start Import
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Importing */}
      {step === 'importing' && (
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Importing Equipment...</h2>

          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm text-gray-600 mb-1">
                <span>{progress.message}</span>
                <span>{progress.current} / {progress.total}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-primary-600 h-2 rounded-full transition-all"
                  style={{ width: `${(progress.current / progress.total) * 100}%` }}
                />
              </div>
            </div>

            <p className="text-sm text-gray-600 text-center">
              Please wait while we import your equipment. This may take a few minutes.
            </p>
          </div>
        </div>
      )}

      {/* Step 4: Complete */}
      {step === 'complete' && importResult && (
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Import Complete</h2>

          <div className="space-y-4">
            <div className={`p-4 border rounded-lg ${
              importResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
            }`}>
              <div className="flex items-start gap-3">
                {importResult.success ? (
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                ) : (
                  <XCircleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                )}
                <div className="flex-1">
                  <p className={`text-sm font-medium ${
                    importResult.success ? 'text-green-900' : 'text-red-900'
                  }`}>
                    Equipment: {importResult.imported} imported
                  </p>
                  {importResult.errors.length > 0 && (
                    <ul className="text-sm text-red-700 mt-2 list-disc list-inside">
                      {importResult.errors.slice(0, 10).map((err, idx) => (
                        <li key={idx}>{err}</li>
                      ))}
                      {importResult.errors.length > 10 && (
                        <li>... and {importResult.errors.length - 10} more errors</li>
                      )}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-3 justify-end mt-6">
            <button onClick={() => navigate('/assets')} className="btn btn-primary">
              View Assets
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
